import { z } from 'zod';

/** Enumerations shared by REST, MCP, web, workers and the database. Values are uppercase English. */
const e = <const T extends readonly [string, ...string[]]>(values: T) => z.enum(values);

export const Locale = e(['en', 'pt-BR', 'es']);
export type Locale = z.infer<typeof Locale>;

export const GlobalRole = e(['ADMIN', 'DEVELOPER', 'PRODUCT']);
export type GlobalRole = z.infer<typeof GlobalRole>;

/** Separately granted capabilities that complement roles (Section 4.2). */
export const Capability = e([
  'REPORTS_TECHNICAL',
  'PROJECT_CREATE',
  'DEVELOPMENT_WORK',
  'ENGINEERING_PROGRESS',
]);
export type Capability = z.infer<typeof Capability>;

export const UserStatus = e(['ACTIVE', 'SUSPENDED', 'HISTORICAL']);
export type UserStatus = z.infer<typeof UserStatus>;

export const Visibility = e(['COMPANY', 'RESTRICTED']);
export type Visibility = z.infer<typeof Visibility>;

export const PrincipalKind = e(['USER', 'GROUP', 'TEAM']);
export type PrincipalKind = z.infer<typeof PrincipalKind>;

export const ResourceKind = e(['PROJECT', 'REQUEST', 'WORK_ITEM', 'COMMENT', 'ATTACHMENT', 'EVIDENCE', 'TEAM']);
export type ResourceKind = z.infer<typeof ResourceKind>;

export const TeamRole = e(['MEMBER', 'MANAGER']);
export type TeamRole = z.infer<typeof TeamRole>;

export const RequestStatus = e([
  'DRAFT',
  'SUBMITTED',
  'NEEDS_INFORMATION',
  'ACCEPTED',
  'REJECTED',
  'DUPLICATE',
  'RESOLVED',
  'NEEDS_ATTENTION',
  'CANCELED',
]);
export type RequestStatus = z.infer<typeof RequestStatus>;

export const RequestAction = e([
  'submit',
  'cancel',
  'ask_information',
  'answer_information',
  'accept',
  'reject',
  'mark_duplicate',
  'fulfill',
  'report_persistence',
  'fulfillment_invalidated',
  'accept_correction',
  'fulfill_after_review',
  'reconsider',
]);
export type RequestAction = z.infer<typeof RequestAction>;

export const Priority = e(['URGENT', 'HIGH', 'NORMAL', 'LOW']);
export type Priority = z.infer<typeof Priority>;

export const WorkType = e([
  'FEATURE',
  'BUG',
  'MAINTENANCE',
  'INFRASTRUCTURE',
  'RESEARCH',
  'ANALYSIS',
  'OPERATIONS',
  'REVIEW',
  'OTHER',
]);
export type WorkType = z.infer<typeof WorkType>;

export const WorkPhase = e(['TODO', 'IN_PROGRESS', 'IN_REVIEW', 'INTEGRATED', 'DONE', 'CANCELED']);
export type WorkPhase = z.infer<typeof WorkPhase>;

export const WorkAction = e([
  'start',
  'request_review',
  'request_changes',
  'integration_observed',
  'production_verified',
  'complete_deliverable',
  'approve_deliverable',
  'cancel',
  'reopen',
  'coverage_lost',
]);
export type WorkAction = z.infer<typeof WorkAction>;

export const CompletionPolicy = e(['PRODUCTION', 'DELIVERABLE', 'REVIEWED_DELIVERABLE']);
export type CompletionPolicy = z.infer<typeof CompletionPolicy>;

export const CompletionBasis = e(['VERIFIED_PRODUCTION', 'DELIVERABLE', 'REVIEWED_DELIVERABLE', 'IMPORTED_SOURCE']);
export type CompletionBasis = z.infer<typeof CompletionBasis>;

export const DeliveryState = e(['UNKNOWN', 'NOT_DEPLOYED', 'PARTIAL', 'DEPLOYED', 'REVERTED']);
export type DeliveryState = z.infer<typeof DeliveryState>;

export const AssociationStatus = e(['CONFIRMED', 'PROVISIONAL']);
export type AssociationStatus = z.infer<typeof AssociationStatus>;

export const EvidenceBasis = e(['AGENT_REPORTED', 'HUMAN_REPORTED', 'PROVIDER_VERIFIED', 'IMPORTED_SOURCE', 'FIXTURE']);
export type EvidenceBasis = z.infer<typeof EvidenceBasis>;

export const EvidenceKind = e([
  'LINK',
  'PULL_REQUEST',
  'COMMIT',
  'CHECK_RESULT',
  'DEPLOYMENT',
  'DOCUMENT',
  'TEST_RESULT',
  'SCREENSHOT',
  'NOTE',
]);
export type EvidenceKind = z.infer<typeof EvidenceKind>;

export const ConfirmationOutcome = e(['UNCONFIRMED', 'CONFIRMED', 'PERSISTS']);
export type ConfirmationOutcome = z.infer<typeof ConfirmationOutcome>;

export const RelationKind = e(['PARENT_OF', 'DEPENDS_ON', 'RELATES_TO', 'DUPLICATES']);
export type RelationKind = z.infer<typeof RelationKind>;

export const SourceChannel = e(['WEB', 'SLACK', 'MCP', 'API', 'EMAIL', 'JIRA_IMPORT', 'SYSTEM']);
export type SourceChannel = z.infer<typeof SourceChannel>;

export const ActorKind = e(['USER', 'AGENT', 'SYSTEM', 'CONNECTOR', 'IMPORT']);
export type ActorKind = z.infer<typeof ActorKind>;

export const AgentClient = e(['CLAUDE_CODE', 'CODEX', 'CLAUDE', 'CHATGPT', 'OTHER']);
export type AgentClient = z.infer<typeof AgentClient>;

export const MilestoneType = e([
  'START',
  'SCOPE_CHANGE',
  'BLOCKER',
  'VERIFICATION',
  'PR_UPDATE',
  'HANDOFF',
  'SESSION_END',
  'NOTE',
]);
export type MilestoneType = z.infer<typeof MilestoneType>;

export const ClaimBasis = e(['AGENT_REPORTED', 'CI_OBSERVED']);
export type ClaimBasis = z.infer<typeof ClaimBasis>;

export const NotificationChannel = e(['IN_APP', 'EMAIL', 'SLACK', 'BROWSER']);
export type NotificationChannel = z.infer<typeof NotificationChannel>;

export const NotificationStatus = e([
  'QUEUED',
  'SENDING',
  'SENT',
  'DELIVERED',
  'BOUNCED',
  'FAILED',
  'DELIVERY_UNKNOWN',
  'SUPPRESSED',
  'CANCELED',
]);
export type NotificationStatus = z.infer<typeof NotificationStatus>;

export const NotificationFrequency = e(['IMMEDIATE', 'DAILY_DIGEST', 'MUTED']);
export type NotificationFrequency = z.infer<typeof NotificationFrequency>;

export const NotificationEvent = e([
  'REQUEST_RECEIVED',
  'REQUEST_NEEDS_INFORMATION',
  'REQUEST_ACCEPTED',
  'REQUEST_REJECTED',
  'REQUEST_WORK_STARTED',
  'REQUEST_RESOLVED',
  'REQUEST_NEEDS_ATTENTION',
  'WORK_ASSIGNED',
  'WORK_BLOCKED',
  'WORK_DONE',
  'COMMENT_ADDED',
  'MENTIONED',
]);
export type NotificationEvent = z.infer<typeof NotificationEvent>;

export const TranslationStatus = e(['READY', 'PENDING', 'FAILED', 'NOT_REQUIRED']);
export type TranslationStatus = z.infer<typeof TranslationStatus>;

export const ScanState = e(['PENDING', 'AVAILABLE', 'REJECTED']);
export type ScanState = z.infer<typeof ScanState>;

export const ParentKind = e(['REQUEST', 'WORK_ITEM']);
export type ParentKind = z.infer<typeof ParentKind>;

export const ConnectorMode = e(['disabled', 'fixture', 'live']);
export type ConnectorMode = z.infer<typeof ConnectorMode>;

export const ReportTier = e(['GENERAL', 'TECHNICAL', 'SENSITIVE']);
export type ReportTier = z.infer<typeof ReportTier>;

export const BlockerAction = e(['OPEN', 'RESOLVE']);
export type BlockerAction = z.infer<typeof BlockerAction>;

export const CreationPolicy = e(['MATCH_ONLY', 'CREATE_IF_NO_MATCH', 'ALLOW_PROVISIONAL']);
export type CreationPolicy = z.infer<typeof CreationPolicy>;

export const ContributionType = e(['IMPLEMENTATION', 'REVIEW', 'RESEARCH', 'OPERATIONS', 'AUTHORING', 'TESTING', 'OTHER']);
export type ContributionType = z.infer<typeof ContributionType>;

export const CustomFieldType = e(['TEXT', 'NUMBER', 'BOOLEAN', 'DATE', 'SINGLE_SELECT', 'MULTI_SELECT']);
export type CustomFieldType = z.infer<typeof CustomFieldType>;
